import React, { useState } from "react";
// TODO: Implement custom API client
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { format } from "date-fns";
import {
  Webhook,
  Search,
  ArrowDownLeft,
  ArrowUpRight,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Clock,
  ChevronLeft,
  ChevronRight,
  Eye,
} from "lucide-react";
import { Input } from "@/shared/components/ui/input";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/shared/components/ui/sheet";
import MerchantLayout from "@/shared/components/merchant/MerchantLayout";
import { useLanguage } from "@/shared/components/shared/LanguageContext";

const statusConfig = {
  received: { icon: Clock, color: "bg-blue-100 text-blue-700" },
  validated: { icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700" },
  processed: { icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700" },
  failed: { icon: XCircle, color: "bg-red-100 text-red-700" },
  invalid_signature: { icon: AlertTriangle, color: "bg-amber-100 text-amber-700" },
  duplicate: { icon: AlertTriangle, color: "bg-orange-100 text-orange-700" },
};

// Helper function to translate status values
const translateStatus = (status, t) => {
  const statusMap = {
    received: "received",
    validated: "validated",
    processed: "processed",
    failed: "failed",
    invalid_signature: "invalidSignature",
    duplicate: "duplicate",
  };
  const translationKey = statusMap[status] || status;
  return t(translationKey) || status;
};

export default function MerchantWebhooks() {
  const { t } = useLanguage();
  const [search, setSearch] = useState("");
  const [directionFilter, setDirectionFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedLog, setSelectedLog] = useState(null);
  const [page, setPage] = useState(0);
  const pageSize = 15;

  // TODO: Implement custom API calls
  const { data: webhooks = [], isLoading } = useQuery({
    queryKey: ["webhook-logs"],
    queryFn: async () => [], // TODO: Implement WebhookLog.list API call
  });

  const filteredWebhooks = webhooks.filter((w) => {
    if (search && !w.webhook_id?.toLowerCase().includes(search.toLowerCase()) &&
        !w.transaction_id?.toLowerCase().includes(search.toLowerCase())) return false;
    if (directionFilter !== "all" && w.direction !== directionFilter) return false;
    if (statusFilter !== "all" && w.status !== statusFilter) return false;
    return true;
  });

  const paginatedWebhooks = filteredWebhooks.slice(page * pageSize, (page + 1) * pageSize);
  const totalPages = Math.ceil(filteredWebhooks.length / pageSize);

  return (
    <MerchantLayout currentPage="MerchantWebhooks" merchantName="Royal Gaming" merchantCode="MRC001">
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t("webhookLogs")}</h1>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 border border-slate-100 flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder={t("searchByWebhookId")}
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              className="pl-10"
            />
          </div>
          <Select value={directionFilter} onValueChange={(v) => { setDirectionFilter(v); setPage(0); }}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder={t("direction")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("allDirection")}</SelectItem>
              <SelectItem value="inbound">{t("inbound")}</SelectItem>
              <SelectItem value="outbound">{t("outbound")}</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(0); }}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder={t("status")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("allStatus")}</SelectItem>
              <SelectItem value="processed">{t("processed")}</SelectItem>
              <SelectItem value="failed">{t("failed")}</SelectItem>
              <SelectItem value="received">{t("received")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  <th className="text-left text-xs font-medium text-slate-500 px-4 py-3">{t("direction")}</th>
                  <th className="text-left text-xs font-medium text-slate-500 px-4 py-3">{t("webhookId")}</th>
                  <th className="text-left text-xs font-medium text-slate-500 px-4 py-3">{t("event")}</th>
                  <th className="text-left text-xs font-medium text-slate-500 px-4 py-3">{t("status")}</th>
                  <th className="text-left text-xs font-medium text-slate-500 px-4 py-3">{t("response")}</th>
                  <th className="text-left text-xs font-medium text-slate-500 px-4 py-3">{t("date")}</th>
                  <th className="text-left text-xs font-medium text-slate-500 px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="border-b border-slate-50">
                      <td colSpan={7} className="px-4 py-4">
                        <div className="h-4 bg-slate-100 rounded animate-pulse" />
                      </td>
                    </tr>
                  ))
                ) : paginatedWebhooks.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-12 text-slate-500">
                      <Webhook className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                      <p>{t("noWebhookLogs")}</p>
                    </td>
                  </tr>
                ) : (
                  paginatedWebhooks.map((log, index) => {
                    const status = statusConfig[log.status] || statusConfig.received;
                    const StatusIcon = status.icon;

                    return (
                      <motion.tr
                        key={log.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: index * 0.02 }}
                        className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer"
                        onClick={() => setSelectedLog(log)}
                      >
                        <td className="px-4 py-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                            log.direction === "inbound" ? "bg-blue-100" : "bg-purple-100"
                          }`}>
                            {log.direction === "inbound" ? (
                              <ArrowDownLeft className="h-4 w-4 text-blue-600" />
                            ) : (
                              <ArrowUpRight className="h-4 w-4 text-purple-600" />
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-mono text-sm text-slate-900">{log.webhook_id}</p>
                          <p className="text-xs text-slate-500">{log.transaction_id}</p>
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="outline" className="text-xs">
                            {log.event_type?.replace(/\./g, " ")}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={status.color}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {translateStatus(log.status, t)}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-sm font-medium ${
                            log.response_status >= 200 && log.response_status < 300 ? "text-emerald-600" : "text-red-600"
                          }`}>
                            {log.response_status || "-"}
                          </span>
                          {log.response_time_ms && (
                            <span className="text-xs text-slate-500 ml-2">{log.response_time_ms}ms</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-500">
                          {log.created_date ? format(new Date(log.created_date), "MMM d, HH:mm:ss") : "-"}
                        </td>
                        <td className="px-4 py-3">
                          <Button variant="ghost" size="sm">
                            <Eye className="h-4 w-4" />
                          </Button>
                        </td>
                      </motion.tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">
              {t("showing")} {page * pageSize + 1} {t("to")} {Math.min((page + 1) * pageSize, filteredWebhooks.length)} {t("of")} {filteredWebhooks.length}
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setPage(page - 1)} disabled={page === 0}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-slate-600">{t("page")} {page + 1} {t("of")} {totalPages}</span>
              <Button variant="outline" size="sm" onClick={() => setPage(page + 1)} disabled={page >= totalPages - 1}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Sheet */}
      <Sheet open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{t("webhookDetails")}</SheetTitle>
          </SheetHeader>
          {selectedLog && (
            <div className="py-6 space-y-6">
              <div>
                <p className="text-sm text-slate-500 mb-1">{t("webhookId")}</p>
                <code className="bg-slate-100 px-3 py-2 rounded-lg font-mono text-sm block">{selectedLog.webhook_id}</code>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">{t("direction")}</p>
                  <p className="font-medium capitalize">{t(selectedLog.direction) || selectedLog.direction}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">{t("event")}</p>
                  <p className="font-medium">{selectedLog.event_type}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">{t("status")}</p>
                  <p className="font-medium capitalize">{translateStatus(selectedLog.status, t)}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">{t("response")}</p>
                  <p className="font-medium">{selectedLog.response_time_ms || 0}ms</p>
                </div>
              </div>
              {selectedLog.url && (
                <div>
                  <p className="text-sm text-slate-500 mb-1">{t("url")}</p>
                  <code className="bg-slate-100 px-3 py-2 rounded-lg font-mono text-xs block break-all">{selectedLog.url}</code>
                </div>
              )}
              {selectedLog.payload && (
                <div>
                  <p className="text-sm text-slate-500 mb-1">{t("payload")}</p>
                  <pre className="bg-slate-900 text-slate-100 p-4 rounded-xl text-xs overflow-x-auto max-h-48">{selectedLog.payload}</pre>
                </div>
              )}
              {selectedLog.response_body && (
                <div>
                  <p className="text-sm text-slate-500 mb-1">{t("responseStatus")}</p>
                  <pre className="bg-slate-900 text-slate-100 p-4 rounded-xl text-xs overflow-x-auto max-h-48">{selectedLog.response_body}</pre>
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </MerchantLayout>
  );
}